import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import CalculateIcon from '@mui/icons-material/Calculate';
import Grid from "@mui/material/Grid";
import Pagination from '@mui/material/Pagination';
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FormControl, FormGroup, FormControlLabel, Checkbox, Button, Menu, MenuItem, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircleIcon from '@mui/icons-material/Circle';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from "@mui/x-date-pickers";
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';

import diffInDays from "../helpers/calculations";
import { convertDateTimeToDate } from "../helpers/dateFormat";
import history from "../../history";
import firstCharToUpper from "../helpers/stringFormat";
import { formatString } from "./valueFunctions";

const getDistinctValues = (array = [], key, transformFn) => {
    if (!Array.isArray(array)) return [];

    // Helper to access nested properties using a key path like "product.name"
    const getValueByPath = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

    const valueCounts = array.reduce((acc, obj) => {
        const value = transformFn ? transformFn(getValueByPath(obj, key)) : getValueByPath(obj, key);

        // Ensure value is valid and convertible to a string
        if (value !== undefined && value !== null) {
            const stringValue = String(value); // Convert to string
            acc[stringValue] = (acc[stringValue] || 0) + 1; // Increment count for this value
        }
        return acc;
    }, {});

    return Object.keys(valueCounts).map((value) => ({ value, count: valueCounts[value] }));
};

const objectsPerPage = 9;

const ValueModelsList = ({ valueModels = [], createNew, preview }) => {
    const [page, setPage] = useState(1);
    const [nameSearch, setNameSearch] = useState(""); // Name search filter
    const [checkedItemsIndustry, setCheckedItemsIndustry] = useState({});
    const [checkedItemsProduct, setCheckedItemsProduct] = useState({});
    const [checkedItemsUpdated, setCheckedItemsUpdated] = useState({});
    // const [isProductVisible, setIsProductVisible] = useState(false);
    const [isIndustryVisible, setIsIndustryVisible] = useState(false);
    const [isUpdatedVisible, setIsUpdatedVisible] = useState(false);
    const [isDateVisible, setIsDateVisible] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [sortedValueModels, setSortedValueModels] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);

    // Access state for auth
    const scopes = useSelector(state => state.auth.scopes)

    useEffect(() => {
        if (Array.isArray(valueModels)) {
            const defaultCheckedItemsIndustry = getDistinctValues(valueModels, 'industry').reduce(
                (acc, option) => ({ ...acc, [option.value]: true }), {}
            );
            setCheckedItemsIndustry(defaultCheckedItemsIndustry);

            const defaultCheckedItemsProduct = getDistinctValues(valueModels, 'product', (product) => product?.name).reduce(
                (acc, option) => ({ ...acc, [option.value]: true }), {}
            );
            setCheckedItemsProduct(defaultCheckedItemsProduct);

            const defaultCheckedItemsUpdated = getDistinctValues(
                valueModels,
                'updated_at',
                (date) => diffInDays(date, Date.now())
            ).reduce(
                (acc, option) => ({ ...acc, [option.value]: true }),
                {}
            );
            setCheckedItemsUpdated(defaultCheckedItemsUpdated);

            setSortedValueModels([...valueModels]);
        }
    }, [valueModels]);

    // const allCheckedProduct = Object.values(checkedItemsProduct).every(Boolean);
    const allCheckedIndustry = Object.values(checkedItemsIndustry).every(Boolean);
    const allCheckedUpdated = Object.values(checkedItemsUpdated).every(Boolean);

    // const handleCheckboxChangeProduct = (event) => {
    //     const { name, checked } = event.target;
    //     setCheckedItemsProduct((prev) => ({ ...prev, [name]: checked }));
    //     setPage(1);
    // };

    const handleCheckboxChangeIndustry = (event) => {
        const { name, checked } = event.target;
        setCheckedItemsIndustry((prev) => ({ ...prev, [name]: checked }));
        setPage(1);
    };

    const handleCheckboxChangeUpdated = (event) => {
        const { name, checked } = event.target;
        setCheckedItemsUpdated((prev) => ({ ...prev, [name]: checked }));
        setPage(1);
    };

    const handleChange = (event, value) => {
        setPage(value);
    };

    const handleSortOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
        setAnchorEl(null);
    };

    const sortOptions = {
        'Name (A-Z)': { key: 'name', direction: 'asc' },
        'Name (Z-A)': { key: 'name', direction: 'desc' },
        // 'Product (A-Z)': { key: 'product', direction: 'asc' },
        // 'Product (Z-A)': { key: 'product', direction: 'desc' },
        'Industry (A-Z)': { key: 'industry', direction: 'asc' },
        'Industry (Z-A)': { key: 'industry', direction: 'desc' },
        'Earliest Updated': { key: 'updated_at', direction: 'asc' },
        'Last Updated': { key: 'updated_at', direction: 'desc' },
        'Oldest': { key: 'created_at', direction: 'asc' },
        'Newest': { key: 'created_at', direction: 'desc' },
    };

    const handleSort = (sortKey, sortDirection) => {
        const sorted = [...valueModels].sort((a, b) => {
            const aValue = sortKey === 'updated_at' || sortKey === 'created_at' ? new Date(a[sortKey]) : a[sortKey];
            const bValue = sortKey === 'updated_at' || sortKey === 'created_at' ? new Date(b[sortKey]) : b[sortKey];

            if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
        });
        setSortedValueModels(sorted);
        setAnchorEl(null);
    };

    const filteredValueModels = sortedValueModels.filter((item) => {
        const isWithinDateRange =
            (!fromDate || new Date(item.created_at) >= new Date(fromDate)) &&
            (!toDate || new Date(item.created_at) <= new Date(toDate));
        const matchesName = item.name.toLowerCase().includes(nameSearch.toLowerCase());

        return (
            checkedItemsIndustry[item.industry] &&
            checkedItemsProduct[item.product?.name] && // Adjusted for product.name
            checkedItemsUpdated[diffInDays(item.updated_at, Date.now())] &&
            isWithinDateRange &&
            matchesName
        );
    });

    const pageCount = Math.ceil(filteredValueModels.length / objectsPerPage);
    const paginatedData = filteredValueModels.slice((page - 1) * objectsPerPage, page * objectsPerPage);

    function renderValueModelObject(valueModel) {
        if (!valueModel) {
            return (
                <Paper sx={{ width: 340, minHeight: 240, padding: 3, ml: 4, mt: 4, position: 'relative' }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Skeleton variant="circular" width={40} height={40} animation={false} />
                        <Typography variant="h6" color="text.secondary">Value Models empty</Typography>
                    </Stack>
                    <Skeleton variant="rounded" width={270} height={80} animation={false} sx={{ mt: 3 }} />
                    <Box sx={{ position: 'absolute', bottom: 8, left: 8 }}>
                        <Skeleton variant="rounded" width={60} height={30} animation={false} sx={{ mt: 3 }} />
                    </Box>
                </Paper>
            )
        };
        return (
            <Paper sx={{ width: 340, minHeight: 240, padding: 3, ml: 4, mt: 4, position: 'relative' }}>
                <Grid container>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <CalculateIcon />
                            <Typography variant="h6">{valueModel.name}</Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Product:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="caption">{firstCharToUpper(valueModel.product.name)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Industry:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="caption">{valueModel.industry}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Step:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="caption">{formatString(valueModel.step)}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Updated:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="caption">
                            {diffInDays(valueModel.updated_at, Date.now())} days ago
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Created:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="caption">
                            {convertDateTimeToDate(valueModel.created_at)}
                        </Typography>
                    </Grid>
                </Grid>
                <Box sx={{ position: 'absolute', bottom: 8, left: 8 }}>
                    <Stack direction="row" gap={1}>
                        {scopes.includes('admin') &&
                            <Button variant="outlined" size="small" onClick={() => history.push(`/value-models/value-model?id=${valueModel.id}`)} sx={{textTransform: 'none'}}>Open</Button>
                        }
                        <Button
                            variant="outlined"
                            startIcon={<WaterfallChartIcon />}
                            size="small"
                            onClick={() => preview(valueModel.id)}
                            color="alternative"
                            sx={{textTransform: 'none'}}
                        >
                            Preview
                        </Button>
                    </Stack>
                </Box>
            </Paper>
        );
    }

    function renderFilterSearch(filterHeading, searchValue, setSearchValue) {
        return (
            <Box sx={{ mt: 2 }}>
                {/* <Typography variant="subtitle1" sx={{ cursor: 'pointer' }}>{filterHeading}</Typography> */}
                <TextField
                    variant="outlined"
                    size="small"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search by Name"
                    fullWidth
                    sx={{ mt: 1 }}
                />
            </Box>
        );
    }

    function renderFilterList(
        filterName,
        filterHeading,
        isFilterVisible,
        setIsFilterVisible,
        allCheckedFilter,
        checkedItemsFilter,
        handleCheckboxChangeFilter,
        transformFn,
        formatLabelFn
    ) {
        // Get distinct values and sort them
        const sortedOptions = getDistinctValues(valueModels, filterName, transformFn)
            .sort((a, b) => {
                // Check if values are numbers for sorting
                const aValue = isNaN(a.value) ? a.value : Number(a.value);
                const bValue = isNaN(b.value) ? b.value : Number(b.value);

                // Sort numerically if numbers, otherwise alphabetically
                return aValue > bValue ? 1 : -1;
            });

        return (
            <Box>
                <Box sx={{ mt: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={() => setIsFilterVisible(!isFilterVisible)}
                    >
                        <Stack direction="row" alignItems="center">
                            <Typography variant="subtitle1" sx={{ cursor: 'pointer' }}>{filterHeading}</Typography>
                            {!allCheckedFilter && (
                                <CircleIcon fontSize="inherit" sx={{ color: 'green', fontSize: '0.5rem', ml: 1 }} />
                            )}
                        </Stack>
                        {isFilterVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Stack>
                </Box>
                {isFilterVisible && (
                    <Box
                        sx={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: '10px',
                        }}
                    >
                        <FormControl component="fieldset">
                            <FormGroup>
                                {sortedOptions.map((option) => (
                                    <FormControlLabel
                                        key={option.value}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={checkedItemsFilter[option.value] || false}
                                                onChange={handleCheckboxChangeFilter}
                                                name={option.value}
                                                sx={{ padding: '3px' }}
                                            />
                                        }
                                        label={formatLabelFn ? `${formatLabelFn(option.value)} (${option.count})` : `${option.value} (${option.count})`}
                                        sx={{
                                            marginBottom: '3px',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.875rem',
                                            },
                                        }}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </Box>
                )}
            </Box>
        );
    }


    function renderFilterDate(filterHeading, isDateVisible, setIsDateVisible, fromDate, toDate, setFromDate, setToDate) {
        return (
            <Box>
                <Box sx={{ mt: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={() => setIsDateVisible(!isDateVisible)}
                    >
                        <Typography variant="subtitle1" sx={{ cursor: 'pointer' }}>{filterHeading}</Typography>
                        {isDateVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Stack>
                </Box>
                {isDateVisible && (
                    <Box sx={{ padding: '10px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="From"
                                value={fromDate}
                                onChange={(newDate) => setFromDate(newDate)}
                                textField={(params) => <TextField {...params} size="small" />}
                                sx={{ width: '20ch', mb: 2 }}
                            />
                            <DatePicker
                                label="To"
                                value={toDate}
                                onChange={(newDate) => setToDate(newDate)}
                                textField={(params) => <TextField {...params} size="small" />}
                                sx={{ width: '20ch' }}
                            />
                        </LocalizationProvider>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 5, ml: 5 }}>
            {/* <Divider orientation="horizontal" flexItem sx={{ mx: 2, height: 'auto' }} /> */}

            <Grid container spacing={2}>
                <Grid item xs={2}>
                    <Stack direction="column">
                        <Box sx={{ mt: 2, mb: 2 }}>
                            {scopes.includes('admin') && createNew()}
                        </Box>
                        {renderFilterSearch('Name', nameSearch, setNameSearch)}
                        {/* {renderFilterList('product.name', 'Product', isProductVisible, setIsProductVisible, allCheckedProduct, checkedItemsProduct, handleCheckboxChangeProduct, product => product, product => firstCharToUpper(product))} */}
                        {renderFilterList('industry', 'Industry', isIndustryVisible, setIsIndustryVisible, allCheckedIndustry, checkedItemsIndustry, handleCheckboxChangeIndustry)}
                        {renderFilterList(
                            'updated_at',
                            'Updated',
                            isUpdatedVisible,
                            setIsUpdatedVisible,
                            allCheckedUpdated,
                            checkedItemsUpdated,
                            handleCheckboxChangeUpdated,
                            (date) => diffInDays(date, Date.now()),
                            (days) => `${days} days ago`
                        )}
                        {renderFilterDate("Created", isDateVisible, setIsDateVisible, fromDate, toDate, setFromDate, setToDate)}
                        {/* <Box sx={{ mt: 10 }}>
                            {createNew()}
                        </Box> */}
                    </Stack>
                </Grid>

                <Grid item xs={8}>
                    <Box sx={{ mt: 2, ml: 4 }}>
                        <Button variant="text" onClick={handleSortOpen} sx={{ textTransform: "none" }}>
                            <SwapVertIcon /> Sort
                        </Button>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleSortClose}>
                            {Object.entries(sortOptions).map(([label, { key, direction }]) => (
                                <MenuItem key={label} onClick={() => handleSort(key, direction)}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    {paginatedData.length > 0 ?
                        <Grid container>
                            {paginatedData.map((valueModel, index) => (
                                <Grid key={index} item>
                                    {renderValueModelObject(valueModel)}
                                </Grid>
                            ))}
                        </Grid>
                        :
                        <Grid container>
                            <Grid item>
                                {renderValueModelObject(null)}
                            </Grid>
                        </Grid>
                    }
                    <Box sx={{ mt: 1, ml: 4 }}>
                        <Pagination count={pageCount || 1} page={page} onChange={handleChange} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ValueModelsList;
